import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw, Send, Check, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  error: Error | null;
  errorId: string;
  componentName: string;
  onReset: () => void;
}

export const ErrorBoundaryFallback = ({ error, errorId, componentName, onReset }: Props) => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const userEmail = user?.email ?? null;

  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const handleSendReport = async () => {
    if (sending || sent) return;
    setSending(true);
    setSendError(null);
    try {
      const composedMessage = [
        `[Auto-captured error report]`,
        `Error ID: ${errorId}`,
        `Component: ${componentName}`,
        `Error: ${error?.message ?? '(no message)'}`,
        `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
        `User agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`,
        '',
        `User said:`,
        reportText.trim() || '(no description provided)',
        '',
        `Stack trace:`,
        (error?.stack ?? '(no stack)').slice(0, 2000),
      ].join('\n');

      const { error: invokeError } = await supabase.functions.invoke('report-issue', {
        body: {
          name: `Error report: ${componentName}`,
          email: userEmail || 'anonymous@cctvplanner.io',
          message: composedMessage,
        },
      });
      if (invokeError) throw invokeError;
      setSent(true);
    } catch (e) {
      setSendError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="alert"
      className="flex items-center justify-center min-h-[300px] p-6 sm:p-8"
    >
      <div className="w-full max-w-md rounded-xl border border-red-200 dark:border-red-900/50 bg-white dark:bg-graphite-900 shadow-sm">
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="shrink-0 mt-0.5">
              <div className="w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-neutral-900 dark:text-white">
                {t('error_something_went_wrong', { defaultValue: 'Something went wrong' })}
              </h2>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                {t('error_auto_logged', {
                  defaultValue: "We've logged this error. You can retry, or tell us what you were doing.",
                })}
              </p>
            </div>
          </div>

          {error?.message && (
            <pre className="text-[11px] font-mono text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-graphite-800 border border-neutral-200 dark:border-brand-border-soft rounded p-2 mb-4 overflow-x-auto whitespace-pre-wrap break-words max-h-24">
              {error.message}
            </pre>
          )}

          {!reportOpen && !sent && (
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={onReset}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[var(--color-brand-blue)] hover:bg-[var(--color-brand-blue-hover)] text-white text-sm font-medium transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                {t('try_again', { defaultValue: 'Try again' })}
              </button>
              <button
                onClick={() => setReportOpen(true)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-neutral-300 dark:border-brand-border-strong hover:bg-neutral-100 dark:hover:bg-neutral-900 text-neutral-700 dark:text-neutral-200 text-sm font-medium transition-colors"
              >
                <Send className="w-4 h-4" />
                {t('error_send_report', { defaultValue: 'Send a report' })}
              </button>
            </div>
          )}

          {reportOpen && !sent && (
            <div className="space-y-3">
              <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300">
                {t('error_what_were_you_doing', {
                  defaultValue: 'What were you doing when this happened? (optional)',
                })}
              </label>
              <textarea
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                rows={3}
                maxLength={2000}
                disabled={sending}
                placeholder={t('error_report_placeholder', {
                  defaultValue: 'e.g. I clicked Export PDF and got this error…',
                })}
                className="w-full px-3 py-2 text-sm rounded-md border border-neutral-300 dark:border-brand-border-strong bg-white dark:bg-graphite-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-600 focus:outline-none focus:border-[var(--color-brand-blue)] resize-none"
              />
              {sendError && (
                <p className="text-xs text-red-600 dark:text-red-400">{sendError}</p>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleSendReport}
                  disabled={sending}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[var(--color-brand-blue)] hover:bg-[var(--color-brand-blue-hover)] disabled:opacity-60 text-white text-sm font-medium transition-colors"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('sending', { defaultValue: 'Sending…' })}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {t('send', { defaultValue: 'Send' })}
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setReportOpen(false);
                    setSendError(null);
                  }}
                  disabled={sending}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-md border border-neutral-300 dark:border-brand-border-strong hover:bg-neutral-100 dark:hover:bg-neutral-900 text-neutral-700 dark:text-neutral-200 text-sm font-medium transition-colors"
                >
                  {t('cancel', { defaultValue: 'Cancel' })}
                </button>
              </div>
            </div>
          )}

          {sent && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/50">
              <Check className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-green-900 dark:text-green-200">
                  {t('error_report_sent', { defaultValue: 'Thanks — report sent' })}
                </p>
                <p className="text-xs text-green-700 dark:text-green-300 mt-0.5">
                  {t('error_report_sent_detail', {
                    defaultValue: "We'll look into it. You can keep using the app or retry.",
                  })}
                </p>
                <button
                  onClick={onReset}
                  className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-green-700 dark:text-green-300 hover:underline"
                >
                  <RefreshCw className="w-3 h-3" />
                  {t('try_again', { defaultValue: 'Try again' })}
                </button>
              </div>
            </div>
          )}

          <p className="mt-4 text-[10px] font-mono text-neutral-400 dark:text-neutral-600 select-all">
            {t('error_id_label', { defaultValue: 'Error ID' })}: {errorId}
          </p>
        </div>
      </div>
    </div>
  );
};
